/**
 * Toast 通知状态管理
 * 用于全局显示通知消息，替代浏览器原生 alert
 */
import { create } from 'zustand';
import {
  type ErrorDisplayContext,
  formatUserFacingError,
} from '@/lib/errors/user-facing-error';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

/** 可选的操作按钮（如“查看”） */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  title?: string;
  details?: string;
  duration?: number;  // 自动关闭时间（毫秒），默认 4000；带操作按钮默认更久
  action?: ToastAction;  // 可选操作按钮
  dedupeKey?: string;  // 相同 key 的 toast 会更新已有项，避免刷屏
}

interface ToastStore {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'> & { id?: string }) => void;
  removeToast: (id: string) => void;
  updateToast: (id: string, message: string) => void;
  clearAllToasts: () => void;
}

// 生成唯一 ID
const generateId = () => `toast_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
const ACTION_TOAST_DURATION_MS = 12000;
const MAX_VISIBLE_TOASTS = 3;

function getDedupeKey(toast: Omit<Toast, 'id'>): string {
  return toast.dedupeKey ?? [
    toast.type,
    toast.title ?? '',
    toast.message,
    toast.details ?? '',
  ].join('|');
}

function trimToToastLimit(toasts: Toast[]): Toast[] {
  if (toasts.length <= MAX_VISIBLE_TOASTS) return toasts;

  const removableIndex = toasts.findIndex(toast => toast.duration !== 0);
  if (removableIndex >= 0) {
    return toasts.filter((_, index) => index !== removableIndex);
  }

  return toasts.slice(-MAX_VISIBLE_TOASTS);
}

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  
  addToast: (toast) => {
    const id = toast.id ?? generateId();
    const dedupeKey = getDedupeKey(toast);
    const newToast: Toast = {
      duration: 4000, // 默认 4 秒
      ...toast,
      id,
      dedupeKey,
    };
    
    set((state) => {
      const existingIndex = state.toasts.findIndex(item => item.dedupeKey === dedupeKey);
      if (existingIndex >= 0) {
        const updated = state.toasts.map((item, index) => (
          index === existingIndex
            ? { ...item, ...newToast, id: item.id }
            : item
        ));
        return { toasts: trimToToastLimit(updated) };
      }

      return { toasts: trimToToastLimit([...state.toasts, newToast]) };
    });
  },
  
  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },

  updateToast: (id, message) => {
    set((state) => ({
      toasts: state.toasts.map((t) => t.id === id ? { ...t, message } : t),
    }));
  },
  
  clearAllToasts: () => {
    set({ toasts: [] });
  },
}));

// 便捷方法，可以直接导入使用
export const toast = {
  success: (message: string, duration?: number) => {
    useToastStore.getState().addToast({ type: 'success', message, duration });
  },
  /** 带操作按钮的成功提醒（如“查看”） */
  successWithAction: (message: string, action: ToastAction, duration?: number) => {
    useToastStore.getState().addToast({ type: 'success', message, action, duration: duration ?? ACTION_TOAST_DURATION_MS });
  },
  error: (message: string, duration?: number) => {
    useToastStore.getState().addToast({ type: 'error', message, duration: duration ?? 6000 });
  },
  showError: (
    error: unknown,
    context: ErrorDisplayContext = 'generic',
    options?: { duration?: number; onAction?: () => void; fallbackMessage?: string; dedupeKey?: string },
  ) => {
    const copy = formatUserFacingError(error, context, options?.fallbackMessage);
    useToastStore.getState().addToast({
      type: copy.severity === 'warning' ? 'warning' : copy.severity === 'info' ? 'info' : 'error',
      title: copy.title,
      message: copy.message,
      details: copy.requestId ? `Request ID: ${copy.requestId}` : undefined,
      duration: options?.duration ?? (copy.retryable ? 8000 : 6500),
      dedupeKey: options?.dedupeKey,
      action: copy.actionLabel && options?.onAction
        ? { label: copy.actionLabel, onClick: options.onAction }
        : undefined,
    });
  },
  warning: (message: string, duration?: number) => {
    useToastStore.getState().addToast({ type: 'warning', message, duration });
  },
  info: (message: string, duration?: number) => {
    useToastStore.getState().addToast({ type: 'info', message, duration });
  },
  /** 持久 toast（不自动消失），返回 dismiss/update 函数 */
  persistent: (message: string, type: ToastType = 'info') => {
    const id = generateId();
    useToastStore.getState().addToast({ id, type, message, duration: 0 });
    return {
      id,
      dismiss: () => useToastStore.getState().removeToast(id),
      update: (msg: string) => useToastStore.getState().updateToast(id, msg),
    };
  },
};
